import React from 'react';

import type { IFormikStageConfigInjectedProps } from '../../FormikStageConfig';
import { AccountService } from '../../../../../account';
import {
  ArtifactTypePatterns,
  excludeAllTypesExcept,
  ExpectedArtifactService,
  StageArtifactSelectorDelegate,
} from '../../../../../artifact';
import { StageConfigField } from '../../common/stageConfigField/StageConfigField';
import type { IArtifact, IExpectedArtifact } from '../../../../../domain';
import { MapEditor } from '../../../../../forms';
import { CheckboxInput, TextInput } from '../../../../../presentation';

export interface IBakeHelmfileConfigFormState {
  gitRepoArtifactAccountNames: string[];
}

export class BakeHelmfileConfigForm extends React.Component<
  IFormikStageConfigInjectedProps,
  IBakeHelmfileConfigFormState
> {
  constructor(props: IFormikStageConfigInjectedProps) {
    super(props);
    this.state = { gitRepoArtifactAccountNames: [] };
  }

  private static readonly excludedArtifactTypes = excludeAllTypesExcept(
    ArtifactTypePatterns.BITBUCKET_FILE,
    ArtifactTypePatterns.CUSTOM_OBJECT,
    ArtifactTypePatterns.EMBEDDED_BASE64,
    ArtifactTypePatterns.GCS_OBJECT,
    ArtifactTypePatterns.GIT_REPO,
    ArtifactTypePatterns.GITHUB_FILE,
    ArtifactTypePatterns.GITLAB_FILE,
    ArtifactTypePatterns.S3_OBJECT,
    ArtifactTypePatterns.HELM_CHART,
    ArtifactTypePatterns.HTTP_FILE,
    ArtifactTypePatterns.ORACLE_OBJECT,
  );

  public componentDidMount() {
    const stage = this.props.formik.values;
    if (stage.inputArtifacts && stage.inputArtifacts.length === 0) {
      this.props.formik.setFieldValue('inputArtifacts', [
        {
          account: '',
          id: '',
        },
      ]);
    }

    // If the Expected Artifact id is provided but the account is not, then attempt to find the artifact from
    // upstream stages and set the account value.
    // This is needed because helmfile file path field will need to be rendered if the artifact has a git repo account type
    const expectedArtifact = this.getInputArtifact(stage, 0);
    if (expectedArtifact.id && !expectedArtifact.account) {
      const availableArtifacts = ExpectedArtifactService.getExpectedArtifactsAvailableToStage(
        stage,
        this.props.pipeline,
      );
      const expectedMatchedArtifact = availableArtifacts.find((a) => a.id === expectedArtifact.id);
      if (expectedMatchedArtifact && expectedMatchedArtifact.matchArtifact) {
        this.props.formik.setFieldValue(
          `inputArtifacts[0].account`,
          expectedMatchedArtifact.matchArtifact.artifactAccount,
        );
      }
    }

    AccountService.getArtifactAccounts().then((artifactAccounts) => {
      this.setState({
        gitRepoArtifactAccountNames: artifactAccounts
          .filter((account) => account.types.some((type) => ArtifactTypePatterns.GIT_REPO.test(type)))
          .map((account) => account.name),
      });
    });
  }

  private onTemplateArtifactEdited = (artifact: IArtifact, index: number) => {
    this.props.formik.setFieldValue(`inputArtifacts[${index}].id`, null);
    this.props.formik.setFieldValue(`inputArtifacts[${index}].artifact`, artifact);
    this.props.formik.setFieldValue(`inputArtifacts[${index}].account`, artifact.artifactAccount);
  };

  private onTemplateArtifactSelected = (artifact: IExpectedArtifact, index: number) => {
    this.props.formik.setFieldValue(`inputArtifacts[${index}].id`, artifact.id);
    this.props.formik.setFieldValue(`inputArtifacts[${index}].artifact`, null);
    // Set the account to matchArtifact.artifactAccount if it exists.
    // This account value will be used to determine if the Helm Chart File Path should be displayed.
    if (artifact.matchArtifact) {
      this.props.formik.setFieldValue(`inputArtifacts[${index}].account`, artifact.matchArtifact.artifactAccount);
    } else {
      this.props.formik.setFieldValue(`inputArtifacts[${index}].account`, null);
    }
  };

  private addInputArtifact = () => {
    const stage = this.props.formik.values;
    const newInputArtifacts = [
      ...stage.inputArtifacts,
      {
        account: '',
        id: '',
      },
    ];

    this.props.formik.setFieldValue('inputArtifacts', newInputArtifacts);
  };

  private removeInputArtifact = (index: number) => {
    const stage = this.props.formik.values;
    const newInputArtifacts = [...stage.inputArtifacts];
    newInputArtifacts.splice(index, 1);
    this.props.formik.setFieldValue('inputArtifacts', newInputArtifacts);
  };

  private getInputArtifact = (stage: any, index: number) => {
    if (!stage.inputArtifacts || stage.inputArtifacts.length === 0) {
      return {
        account: '',
        id: '',
      };
    } else {
      return stage.inputArtifacts[index];
    }
  };

  private onStateValuesArtifactEdited = (artifact: IArtifact, index: number) => {
    this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].id`, null);
    this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].artifact`, artifact);
    this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].account`, artifact.artifactAccount);
  };

  private onStateValuesArtifactSelected = (artifact: IExpectedArtifact, index: number) => {
    this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].id`, artifact.id);
    this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].artifact`, null);
    if (artifact.matchArtifact) {
      this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].account`, artifact.matchArtifact.artifactAccount);
    } else {
      this.props.formik.setFieldValue(`stateValuesArtifacts[${index}].account`, null);
    }
  };

  private addStateValuesArtifact = () => {
    const stage = this.props.formik.values;
    const newStateValuesArtifacts = [
      ...(stage.stateValuesArtifacts || []),
      {
        account: '',
        id: '',
      },
    ];

    this.props.formik.setFieldValue('stateValuesArtifacts', newStateValuesArtifacts);
  };

  private removeStateValuesArtifact = (index: number) => {
    const stage = this.props.formik.values;
    const newStateValuesArtifacts = [...stage.stateValuesArtifacts];
    newStateValuesArtifacts.splice(index, 1);
    this.props.formik.setFieldValue('stateValuesArtifacts', newStateValuesArtifacts);
  };

  private outputNameChange = (outputName: string) => {
    const stage = this.props.formik.values;
    const expectedArtifacts = stage.expectedArtifacts;
    if (
      expectedArtifacts &&
      expectedArtifacts.length === 1 &&
      expectedArtifacts[0].matchArtifact &&
      expectedArtifacts[0].matchArtifact.type === 'embedded/base64'
    ) {
      this.props.formik.setFieldValue('expectedArtifacts', [
        {
          ...expectedArtifacts[0],
          matchArtifact: {
            ...expectedArtifacts[0].matchArtifact,
            name: outputName,
          },
        },
      ]);
    }
  };

  private overrideChanged = (overrides: any) => {
    this.props.formik.setFieldValue('overrides', overrides);
  };

  public render() {
    const stage = this.props.formik.values;
    return (
      <>
        <h4>Helmfile Options</h4>
        <StageConfigField fieldColumns={3} label={'Name'} helpKey="pipeline.config.bake.manifest.helmfile.name">
          <TextInput
            onChange={(e: React.ChangeEvent<any>) => {
              this.props.formik.setFieldValue('outputName', e.target.value);
              this.outputNameChange(e.target.value);
            }}
            value={stage.outputName}
          />
        </StageConfigField>
        <StageConfigField
          fieldColumns={3}
          label={'Namespace'}
          helpKey="pipeline.config.bake.manifest.helmfile.namespace"
        >
          <TextInput
            onChange={(e: React.ChangeEvent<any>) => {
              this.props.formik.setFieldValue('namespace', e.target.value);
            }}
            value={stage.namespace}
          />
        </StageConfigField>
        <StageConfigField
          fieldColumns={3}
          label={'Environment'}
          helpKey="pipeline.config.bake.manifest.helmfile.environment"
        >
          <TextInput
            onChange={(e: React.ChangeEvent<any>) => {
              this.props.formik.setFieldValue('environment', e.target.value);
            }}
            value={stage.environment}
          />
        </StageConfigField>
        <h4>Template Artifact</h4>
        <StageArtifactSelectorDelegate
          artifact={this.getInputArtifact(stage, 0).artifact}
          excludedArtifactTypePatterns={BakeHelmfileConfigForm.excludedArtifactTypes}
          expectedArtifactId={this.getInputArtifact(stage, 0).id}
          helpKey="pipeline.config.bake.manifest.expectedArtifact"
          label="Expected Artifact"
          onArtifactEdited={(artifact) => {
            this.onTemplateArtifactEdited(artifact, 0);
          }}
          onExpectedArtifactSelected={(artifact: IExpectedArtifact) => this.onTemplateArtifactSelected(artifact, 0)}
          pipeline={this.props.pipeline}
          stage={stage}
        />
        {this.state.gitRepoArtifactAccountNames.includes(this.getInputArtifact(stage, 0).account) && (
          <StageConfigField label="Helmfile File Path" helpKey="pipeline.config.bake.manifest.helmfile.filePath">
            <TextInput
              onChange={(e: React.ChangeEvent<any>) => {
                this.props.formik.setFieldValue('helmfileFilePath', e.target.value);
              }}
              value={stage.helmfileFilePath}
            />
          </StageConfigField>
        )}
        <h4>Overrides</h4>
        {stage.inputArtifacts && stage.inputArtifacts.length > 1 && (
          <div className="row form-group">
            {stage.inputArtifacts.slice(1).map((a: any, index: number) => {
              return (
                <div key={index}>
                  <div className="col-md-offset-1 col-md-9">
                    <StageArtifactSelectorDelegate
                      artifact={a.artifact}
                      excludedArtifactTypePatterns={[]}
                      expectedArtifactId={a.id}
                      label="Expected Artifact"
                      onArtifactEdited={(artifact) => {
                        this.onTemplateArtifactEdited(artifact, index + 1);
                      }}
                      onExpectedArtifactSelected={(artifact: IExpectedArtifact) =>
                        this.onTemplateArtifactSelected(artifact, index + 1)
                      }
                      pipeline={this.props.pipeline}
                      stage={stage}
                    />
                  </div>
                  <div className="col-md-1">
                    <div className="form-control-static">
                      <button onClick={() => this.removeInputArtifact(index + 1)}>
                        <span className="glyphicon glyphicon-trash" />
                        <span className="sr-only">Remove field</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <StageConfigField fieldColumns={8} label={''}>
          <button className="btn btn-block btn-sm add-new" onClick={() => this.addInputArtifact()}>
            <span className="glyphicon glyphicon-plus-sign" />
            Add value artifact
          </button>
        </StageConfigField>
        <h4>State Values</h4>
        {stage.stateValuesArtifacts && stage.stateValuesArtifacts.length > 0 && (
          <div className="row form-group">
            {stage.stateValuesArtifacts.map((a: any, index: number) => {
              return (
                <div key={index}>
                  <div className="col-md-offset-1 col-md-9">
                    <StageArtifactSelectorDelegate
                      artifact={a.artifact}
                      excludedArtifactTypePatterns={[]}
                      expectedArtifactId={a.id}
                      label="Expected Artifact"
                      onArtifactEdited={(artifact) => {
                        this.onStateValuesArtifactEdited(artifact, index);
                      }}
                      onExpectedArtifactSelected={(artifact: IExpectedArtifact) =>
                        this.onStateValuesArtifactSelected(artifact, index)
                      }
                      pipeline={this.props.pipeline}
                      stage={stage}
                    />
                  </div>
                  <div className="col-md-1">
                    <div className="form-control-static">
                      <button onClick={() => this.removeStateValuesArtifact(index)}>
                        <span className="glyphicon glyphicon-trash" />
                        <span className="sr-only">Remove field</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <StageConfigField fieldColumns={8} label={''} helpKey="pipeline.config.bake.manifest.helmfile.stateValues">
          <button className="btn btn-block btn-sm add-new" onClick={() => this.addStateValuesArtifact()}>
            <span className="glyphicon glyphicon-plus-sign" />
            Add state values artifact
          </button>
        </StageConfigField>
        <StageConfigField fieldColumns={6} label="Overrides">
          {stage.overrides && (
            <MapEditor
              addButtonLabel={'Add override'}
              model={stage.overrides}
              allowEmpty={true}
              onChange={(o: any) => this.overrideChanged(o)}
            />
          )}
        </StageConfigField>
        <StageConfigField
          fieldColumns={6}
          helpKey={'pipeline.config.bake.manifest.helm.includeCRDs'}
          label="Include CRDs"
        >
          <CheckboxInput
            value={stage.includeCRDs}
            text={''}
            onChange={() => this.props.formik.setFieldValue('includeCRDs', !stage.includeCRDs)}
          />
        </StageConfigField>
        <StageConfigField
          fieldColumns={6}
          helpKey={'pipeline.config.bake.manifest.overrideExpressionEvaluation'}
          label="Expression Evaluation"
        >
          <CheckboxInput
            value={stage.evaluateOverrideExpressions}
            text={'Evaluate SpEL expressions in overrides at bake time'}
            onChange={() =>
              this.props.formik.setFieldValue('evaluateOverrideExpressions', !stage.evaluateOverrideExpressions)
            }
          />
        </StageConfigField>
      </>
    );
  }
}
